import {
	Box,
	Button,
	Drawer,
	Flex,
	Grid,
	Input,
	InputCheckBox,
	RiAddBoxLine,
	RiDownload2Line
} from "@itandi/itandi-bb-ui";
import { SingleComboBox } from "@itandi/itandi-bb-ui/SingleComboBox";
import { useRouter } from "next/router";
import type { ChangeEvent, ComponentProps } from "react";
import { useCallback, useState } from "react";
import { FormProvider, useForm } from "react-hook-form";
import DealsCsvExportModal from "~/components/common/dealsCsvExports/DealsCsvExportModal";
import { renderSearchButton } from "~/components/common/parts/search/SearchButton";
import { AdvancedSearchButton } from "~/components/deprecated/AdvancedSearchButton";
import { DealSearchDetailTab } from "~/components/specifics/deals/index/search/DealSearchDetailTab";
import { useDebounce } from "~/hooks/useDebounce";
import useItandiAccount from "~/hooks/useItandiAccount";
import type { CompanyVendor } from "~/models/CompanyVendor";
import { convertToComboBoxOption } from "~/models/CompanyVendor";
import type { DealSearchCondition } from "~/models/Deal";
import { searchDetailBadge } from "~/models/Deal";
import { PATH as NEW_PATH } from "~/pages/deals/new";
import { requestCsvGenerate } from "~/utils/api/CsvGenerateResult";
import Styles from "./DealSearch.module.scss";

type Props = {
	companyVendors: CompanyVendor[];
	dealSearchCondition: DealSearchCondition;
	handleChangeCondition: (newCondition: DealSearchCondition) => Promise<void>;
	isSp: boolean;
};

export function DealSearch({
	dealSearchCondition,
	handleChangeCondition,
	companyVendors,
	isSp
}: Props) {
	const { data: account } = useItandiAccount().currentAccount();
	const methods = useForm<DealSearchCondition>({
		defaultValues: dealSearchCondition,
		mode: "onChange"
	});
	const router = useRouter();
	const [propertyDealSearchValues, setPropertyDealSearchValues] = useState({
		name: dealSearchCondition.name ?? "",
		roomNumber: dealSearchCondition.roomNumber ?? ""
	});
	const [isOpenSearchDetailTab, setIsOpenSearchDetailTab] = useState(false);
	const selectedCompanyVendor =
		companyVendors.find(
			({ id }) => id === dealSearchCondition.companyVendorId
		) ?? null;
	const [selectedCompanyVendorOption, setSelectedCompanyVendorOption] =
		useState<ComponentProps<typeof SingleComboBox>["value"]>(
			selectedCompanyVendor
				? convertToComboBoxOption(selectedCompanyVendor)
				: null
		);
	const [isPrioritySort, setIsPrioritySort] = useState(
		dealSearchCondition.prioritySort ?? true
	);
	const { reset } = methods;
	const handleOpenSearchDetailTab = useCallback((): void => {
		// 確定済み条件をセットする
		reset(dealSearchCondition);
		setIsOpenSearchDetailTab(true);
	}, [reset, dealSearchCondition]);
	const handleCloseSearchDetailTab = useCallback((): void => {
		setIsOpenSearchDetailTab(false);
	}, []);
	const debounce = useDebounce(200);
	const createOnChange = useCallback(
		(field: keyof Pick<DealSearchCondition, "name" | "roomNumber">) =>
			async (e: ChangeEvent<HTMLInputElement>): Promise<void> => {
				const newValue = e.target.value;
				const newCondition: DealSearchCondition = {
					...dealSearchCondition,
					[field]: newValue
				};
				setPropertyDealSearchValues((prev) => ({ ...prev, [field]: newValue }));
				debounce(() => {
					handleChangeCondition(newCondition).then();
				});
			},
		[dealSearchCondition, debounce, handleChangeCondition]
	);
	const onChangeName = createOnChange("name");
	const onChangeRoomNumber = createOnChange("roomNumber");
	const onChangeCompanyVendor = useCallback(
		async (
			option: ComponentProps<typeof SingleComboBox>["value"]
		): Promise<void> => {
			setSelectedCompanyVendorOption(option);
			const newCondition: DealSearchCondition = {
				...dealSearchCondition,
				companyVendorId: option?.value ? Number(option.value) : undefined
			};
			await handleChangeCondition(newCondition);
		},
		[dealSearchCondition, handleChangeCondition]
	);
	// 簡易検索の入力欄に詳細検索で入力した値を適用する
	const applyConditionToInstantSearch = useCallback(
		(newCondition: DealSearchCondition): void => {
			setPropertyDealSearchValues({
				name: newCondition.name ?? "",
				roomNumber: newCondition.roomNumber ?? ""
			});
			const newSelectedCompanyVendor = companyVendors.find(
				(companyVendor) => companyVendor.id === newCondition.companyVendorId
			);
			setSelectedCompanyVendorOption(
				newSelectedCompanyVendor
					? convertToComboBoxOption(newSelectedCompanyVendor)
					: null
			);
			setIsPrioritySort(newCondition.prioritySort ?? true);
		},
		[companyVendors]
	);
	const handleDetailSearch = useCallback(async (): Promise<void> => {
		const newCondition = methods.getValues();
		// statusId が空配列の場合は「未指定」として扱う
		// 検索後に詳細検索を開いた時にデフォルト選択へ復帰させるため
		if (
			Array.isArray(newCondition.statusId) &&
			newCondition.statusId.length === 0
		) {
			delete newCondition.statusId;
		}
		applyConditionToInstantSearch(newCondition);
		handleCloseSearchDetailTab();
		await handleChangeCondition(newCondition);
	}, [
		methods,
		applyConditionToInstantSearch,
		handleChangeCondition,
		handleCloseSearchDetailTab
	]);
	const [csvExportModalOpen, setCsvExportModalOpen] = useState(false);
	const handleOpenCsvExport = useCallback(() => {
		setCsvExportModalOpen(true);
	}, []);
	const handleCloseCsvExport = useCallback(() => {
		setCsvExportModalOpen(false);
	}, []);
	return (
		<div className={Styles.SimpleSearchContainer}>
			{isSp ? (
				<>
					<Grid gap={8}>
						<Input
							onChange={onChangeName}
							placeholder="物件名"
							type="search"
							value={propertyDealSearchValues.name}
						/>
						<Input
							onChange={onChangeRoomNumber}
							placeholder="部屋番号"
							type="search"
							value={propertyDealSearchValues.roomNumber}
						/>
					</Grid>
					<Flex justifyContent="flex-end" marginTop={8}>
						<AdvancedSearchButton
							badge={searchDetailBadge(dealSearchCondition)}
							onClick={handleOpenSearchDetailTab}
						/>
					</Flex>
				</>
			) : (
				<Flex
					alignItems="center"
					justifyContent="space-between"
					marginBottom={8}
				>
					<Grid
						alignItems="center"
						autoFlow="column"
						gap={8}
						justifyContent="start"
					>
						<Box width={240}>
							<Input
								onChange={onChangeName}
								placeholder="物件名"
								type="search"
								value={propertyDealSearchValues.name}
							/>
						</Box>
						<Box width={120}>
							<Input
								onChange={onChangeRoomNumber}
								placeholder="部屋番号"
								type="search"
								value={propertyDealSearchValues.roomNumber}
							/>
						</Box>
						<Box width={180}>
							<SingleComboBox
								onChange={onChangeCompanyVendor}
								options={companyVendors.map((c: CompanyVendor) =>
									convertToComboBoxOption(c)
								)}
								placeholder="施工会社"
								value={selectedCompanyVendorOption}
							/>
						</Box>
						<Box>
							<AdvancedSearchButton
								badge={searchDetailBadge(dealSearchCondition)}
								onClick={handleOpenSearchDetailTab}
							/>
						</Box>
						<Box>
							<InputCheckBox
								checked={isPrioritySort}
								label="未読を優先表示する"
								onChange={(e): void => {
									const newChecked = e.target.checked;
									setIsPrioritySort(newChecked);
									const newCondition: DealSearchCondition = {
										...dealSearchCondition,
										prioritySort: newChecked
									};
									handleChangeCondition(newCondition).then();
								}}
							/>
						</Box>
					</Grid>
					<Grid
						alignItems="center"
						autoFlow="column"
						gap={8}
						justifyContent="end"
					>
						{account && !account.isAdmin ? (
							<>
								<Button
									onClick={handleOpenCsvExport}
									startIcon={<RiDownload2Line />}
									variant="primary"
								>
									CSV出力
								</Button>
								<DealsCsvExportModal
									handleClose={handleCloseCsvExport}
									isOpen={csvExportModalOpen}
									requestCsvGenerate={requestCsvGenerate}
									searchParams={dealSearchCondition}
								/>
							</>
						) : null}
						<Button
							onClick={(): void => {
								router.push(NEW_PATH);
							}}
							startIcon={<RiAddBoxLine />}
							variant="primary"
						>
							追加
						</Button>
					</Grid>
				</Flex>
			)}
			<div className={Styles.SearchDetailContainer}>
				<Drawer
					bottomArea={renderSearchButton(handleDetailSearch)}
					isOpen={isOpenSearchDetailTab}
					onClickBackdrop={handleCloseSearchDetailTab}
					onClickCloseButton={handleCloseSearchDetailTab}
					title="詳細検索"
				>
					<FormProvider
						{...methods}
						key={isOpenSearchDetailTab ? "open" : "close"}
					>
						<DealSearchDetailTab companyVendors={companyVendors} />
					</FormProvider>
				</Drawer>
			</div>
		</div>
	);
}
